import { CellContext } from '@tanstack/react-table';
import { LearnerProgress } from '.';
import {
  TooltipContent,
  TooltipDetail,
  TooltipDetailHighlight,
  TooltipTitle,
} from './Tooltip';
import { SmallPortalTooltip } from '../../../components/Tooltip';
import styled from 'styled-components';
import { LEARNER_STATUS } from '../../../api/admin';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

export const ProgressBarContainer = styled.div`
  height: 8px;
  width: 100px;
  background-color: ${({ theme }) => theme.colors.bgDefault};
  border-radius: 4px;
  overflow: hidden;
  display: inline-block;
  margin-left: 8px;
`;

export const ProgressBarFill = styled.div<{ progress: number }>`
  height: 100%;
  width: ${({ progress }) => progress}%;
  background-color: ${({ theme }) => theme.colors.buttonPrimary};
  border-radius: 4px;
  transition: width 0.3s ease-in-out;
`;

export const ProgressCellContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
`;

export default function ProgressCell(
  info: CellContext<LearnerProgress, unknown>
) {
  const value = info.row.original.planProgress;
  const status = info.row.original.status;
  const isInvited =
    status === LEARNER_STATUS.INVITED ||
    status === LEARNER_STATUS.INVITE_EXPIRED;

  if (isInvited || value === null) {
    return <div style={{ textAlign: 'right' }}>-</div>;
  }

  const uniqueId = `plan-progress-tooltip-${info.row.id}-${info.column.id}`;
  const sortedSubmissions = info.row.original.progress.submissions.sort(
    (a, b) =>
      new Date(b.submissionDate).getTime() -
      new Date(a.submissionDate).getTime()
  );
  return (
    <div
      data-tooltip-id={uniqueId}
      style={{
        width: '100%',
        display: 'flex',
        justifyContent: 'flex-end',
      }}
    >
      <ProgressCellContent>
        <ProgressBarContainer>
          <ProgressBarFill progress={value} />
        </ProgressBarContainer>
      </ProgressCellContent>
      <SmallPortalTooltip anchorSelect={`[data-tooltip-id="${uniqueId}"]`}>
        <TooltipContent>
          <TooltipTitle>Latest Plan Progress</TooltipTitle>
          <TooltipDetail>{value}%</TooltipDetail>
          {sortedSubmissions.length > 0 && (
            <>
              <TooltipTitle style={{ marginTop: 8 }}>
                Latest Submissions
              </TooltipTitle>
              {sortedSubmissions.map((s, index) => (
                <TooltipDetail key={s.submissionId}>
                  {index + 1}.{' '}
                  <TooltipDetailHighlight>{s.name}</TooltipDetailHighlight> -{' '}
                  {dayjs.utc(s.submissionDate).fromNow()}
                </TooltipDetail>
              ))}
            </>
          )}
        </TooltipContent>
      </SmallPortalTooltip>
    </div>
  );
}
